import {
  Alert, Box, Button, CircularProgress, IconButton, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { colors } from 'constants/theme'
import { useTranslation } from 'react-i18next'
import AboutUs from 'api/admin/clinic-manage/about.api'
import DashboardConfigsApi, { OccupationGroupBucket } from 'api/executive/dashboard-configs.api'
import { useCareers, CareerRef } from 'features/executive/shared/hooks/use-reference-data'
import ButtonCustom from 'component/Button/ButtonCustom'
import InputTextField from 'component/Input/InputTextField'
import SelectCheckBoxInput from 'new-components/inputs/select_checkbox_input'
import PageModal from 'new-components/modal/page-modal'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import { icons } from 'constants/images'
import InfoBanner from 'new-components/banners/info-banner'
import TabLoading from './TabLoading'

/** careerIds are conceptually a Set — compare ignoring order. */
const bucketsEqual = (a: OccupationGroupBucket[], b: OccupationGroupBucket[]) => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i].label !== b[i].label) return false
    if (a[i].careerIds.length !== b[i].careerIds.length) return false
    const sa = [...a[i].careerIds].sort((x, y) => x - y)
    const sb = [...b[i].careerIds].sort((x, y) => x - y)
    for (let j = 0; j < sa.length; j++) if (sa[j] !== sb[j]) return false
  }
  return true
}

/**
 * อาชีพ — admin-tunable occupation grouping used by the Insight
 * dashboard's occupation widget. Each bucket bundles N careerIds
 * under a single display label so the dashboard can show a small,
 * clinic-meaningful breakdown instead of the full long-tail list.
 *
 * Constraints (enforced server-side too):
 *   - bucket.label must be non-empty and unique across buckets
 *   - bucket.careerIds must be non-empty
 *   - each careerId may appear in at most one bucket (no double-count)
 */
export default function OccupationGroupsTab() {
  const { t } = useTranslation()
  const { data: careers, loading: careersLoading } = useCareers()
  const [aboutUsId, setAboutUsId] = useState<number | null>(null)
  const [buckets, setBuckets] = useState<OccupationGroupBucket[]>([])
  const [baseline, setBaseline] = useState<OccupationGroupBucket[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isConfigured, setIsConfigured] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [confirmReset, setConfirmReset] = useState(false)

  // Resolve aboutUsId once. Surface failures so the user can tell why the
  // Save button is permanently disabled (was a silent failure before).
  useEffect(() => {
    AboutUs.findAboutUs()
      .then((res) => {
        const id = Number((res?.data as { aboutUsId?: number })?.aboutUsId)
        if (Number.isFinite(id) && id > 0) {
          setAboutUsId(id)
        } else {
          // eslint-disable-next-line no-console
          console.warn('[OccupationGroupsTab] AboutUs.findAboutUs returned no aboutUsId — response:', res?.data)
          notiError(t('DASHBOARD_TARGETS.AGE.NO_ABOUT_US', {
            defaultValue: 'โหลดข้อมูลคลินิกไม่ได้ — บันทึกชั่วคราวไม่สามารถใช้งานได้',
          }) as string)
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[OccupationGroupsTab] AboutUs.findAboutUs failed:', e)
        notiError(t('DASHBOARD_TARGETS.AGE.ABOUT_US_ERROR', {
          defaultValue: 'โหลดข้อมูลคลินิกล้มเหลว — ลองรีโหลดหน้าเว็บ',
        }) as string)
      })
  }, [])

  // Load buckets when aboutUsId is available
  useEffect(() => {
    if (aboutUsId == null) return
    setLoading(true)
    DashboardConfigsApi.getOccupationGroups(aboutUsId)
      .then((r) => {
        setBuckets(r.buckets)
        setBaseline(r.buckets)
        setIsConfigured(r.isConfigured)
      })
      .catch(() => {
        setBuckets([])
        setBaseline([])
      })
      .finally(() => setLoading(false))
  }, [aboutUsId])

  // Quick lookup: id → name, plus the set of already-claimed careerIds so we can
  // dim them in other buckets' autocompletes (each career belongs to one bucket).
  const careerById = useMemo(() => {
    const m = new Map<number, CareerRef>()
    for (const c of careers) m.set(c.id, c)
    return m
  }, [careers])

  const setBucket = (i: number, patch: Partial<OccupationGroupBucket>) => {
    setBuckets((prev) => prev.map((b, idx) => (idx === i ? { ...b, ...patch } : b)))
  }

  const addRow = () => {
    setBuckets((prev) => [...prev, { label: '', careerIds: [] }])
  }

  const removeRow = (i: number) => {
    setBuckets((prev) => prev.filter((_, idx) => idx !== i))
  }

  const hasUnsavedChanges = useMemo(() => !bucketsEqual(buckets, baseline), [buckets, baseline])

  const saveDisabledReason: string | null = saving
    ? (t('DASHBOARD_TARGETS.AGE.SAVE_IN_FLIGHT', { defaultValue: 'กำลังบันทึก...' }) as string)
    : aboutUsId == null
      ? (t('DASHBOARD_TARGETS.AGE.NO_ABOUT_US_SHORT', { defaultValue: 'ยังโหลดข้อมูลคลินิกไม่สำเร็จ' }) as string)
      : !hasUnsavedChanges
        ? (t('DASHBOARD_TARGETS.AGE.NOTHING_TO_SAVE', { defaultValue: 'ยังไม่มีการเปลี่ยนแปลง' }) as string)
        : null

  const validate = (): string | null => {
    if (buckets.length === 0) return t('DASHBOARD_TARGETS.OCC.NEED_ONE', 'ต้องมีกลุ่มอย่างน้อย 1 รายการ') as string
    const seenLabels = new Set<string>()
    const seenIds = new Set<number>()
    for (const [i, b] of buckets.entries()) {
      const row = i + 1
      const label = b.label.trim()
      if (!label) return `แถวที่ ${row}: ต้องตั้งชื่อกลุ่ม`
      if (seenLabels.has(label)) return `แถวที่ ${row}: ชื่อกลุ่มซ้ำ "${label}"`
      seenLabels.add(label)
      if (b.careerIds.length === 0) return `แถวที่ ${row}: เลือกอาชีพอย่างน้อย 1 รายการ`
      for (const id of b.careerIds) {
        if (seenIds.has(id)) return `แถวที่ ${row}: อาชีพ "${careerById.get(id)?.name ?? `#${id}`}" ถูกใช้ในกลุ่มอื่นแล้ว`
        seenIds.add(id)
      }
    }
    return null
  }

  /** Roll the in-progress buckets back to whatever was last loaded from
   *  the server. Triggered from the toolbar's "รีเซ็ต" button after the
   *  confirm dialog so an accidental click can't blow away edits. */
  const doReset = () => {
    setBuckets(baseline)
    setValidationError(null)
    notiSuccess(t('DASHBOARD_TARGETS.RESET_SUCCESS', { defaultValue: 'รีเซ็ตการแก้ไขแล้ว' }) as string)
  }

  const save = async () => {
    if (aboutUsId == null) return
    const err = validate()
    if (err) {
      setValidationError(err)
      return
    }
    setValidationError(null)
    setSaving(true)
    try {
      // Trim labels before sending so server doesn't see leading/trailing spaces.
      const payload = buckets.map((b) => ({ label: b.label.trim(), careerIds: b.careerIds }))
      await DashboardConfigsApi.saveOccupationGroups(aboutUsId, payload)
      setBaseline(payload)
      setIsConfigured(true)
      notiSuccess(t('DASHBOARD_TARGETS.SAVE_SUCCESS') as string)
    } catch (e) {
      const msg = (e as any)?.response?.data?.message
      notiError(typeof msg === 'string' ? msg : (t('DASHBOARD_TARGETS.SAVE_ERROR') as string))
    } finally {
      setSaving(false)
    }
  }

  if (loading || careersLoading) {
    return <TabLoading />
  }

  return (
    <Box>
      <InfoBanner sx={{ mb: 2 }}>
        {t(
          'DASHBOARD_TARGETS.OCC.DESCRIPTION',
          'กำหนดกลุ่มอาชีพที่ใช้แสดงในแดชบอร์ด Insight → อาชีพ — แต่ละกลุ่มสามารถรวมหลายอาชีพไว้ด้วยกัน (เช่น "พนักงานออฟฟิศ" = พนักงานบริษัทเอกชน + employee + ...)'
        )}
        {!isConfigured && (
          <> — {t(
            'DASHBOARD_TARGETS.OCC.NO_CONFIG',
            'ยังไม่ได้ตั้งค่า — แดชบอร์ดกำลังแสดง Top 8 อาชีพอัตโนมัติ กดบันทึกหลังเพิ่มกลุ่มเพื่อให้แดชบอร์ดใช้การจัดกลุ่มของคลินิก'
          )}</>
        )}
      </InfoBanner>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'flex-end' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <ButtonCustom
            variant="outlined"
            onClick={() => setConfirmReset(true)}
            disabled={saving || !hasUnsavedChanges}
            textButton={t('DASHBOARD_TARGETS.RESET_BUTTON', { defaultValue: 'รีเซ็ต' })}
          />
          <Tooltip title={saveDisabledReason ?? ''} placement="top">
            <span>
              <ButtonCustom
                variant="contained"
                onClick={save}
                disabled={Boolean(saveDisabledReason)}
                textButton={saving ? <CircularProgress size={20} /> : t('DASHBOARD_TARGETS.SAVE')}
              />
            </span>
          </Tooltip>
        </Box>
      </Box>

      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setValidationError(null)}>
          {validationError}
        </Alert>
      )}

      <Paper sx={{ overflow: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }} align="center">#</TableCell>
              <TableCell sx={{ width: 240 }}>{t('DASHBOARD_TARGETS.OCC.LABEL', 'ชื่อกลุ่ม')}</TableCell>
              <TableCell>{t('DASHBOARD_TARGETS.OCC.CAREERS', 'อาชีพที่รวมในกลุ่ม')}</TableCell>
              <TableCell sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Add-row affordance matches IMPORT_PRODUCT.FORM.ADD_NEW_PRODUCT pattern:
                a TableRow spanning the full width, with a text-variant Button
                left-aligned at the top of the body. */}
            <TableRow>
              <TableCell
                colSpan={4}
                align="left"
                onClick={() => { if (!saving) addRow() }}
                sx={{ p: 1, cursor: saving ? 'default' : 'pointer' }}
              >
                <Button
                  variant="text"
                  disabled={saving}
                  sx={{
                    color: colors.themeSecondColor,
                    textTransform: 'none',
                    fontWeight: 500,
                    minWidth: 0,
                    p: 0,
                    '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  <span style={{ marginRight: 4 }}>+</span>
                  {t('DASHBOARD_TARGETS.OCC.ADD_ROW', 'เพิ่มกลุ่ม')}
                </Button>
              </TableCell>
            </TableRow>
            {buckets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  {t('DASHBOARD_TARGETS.OCC.EMPTY', 'ยังไม่มีกลุ่ม — กด "เพิ่มกลุ่ม"')}
                </TableCell>
              </TableRow>
            ) : (
              buckets.map((b, i) => {
                // Options = all careers; mark those claimed by *other* rows as disabled
                // (so users can see them greyed out instead of having them vanish).
                const claimedByOthers = new Set<number>()
                buckets.forEach((other, oi) => {
                  if (oi === i) return
                  other.careerIds.forEach((id) => claimedByOthers.add(id))
                })
                const options = careers.map((c) => ({
                  id: c.id,
                  name: c.name,
                  disabled: claimedByOthers.has(c.id),
                }))

                return (
                  <TableRow key={i} hover sx={{ verticalAlign: 'top' }}>
                    <TableCell align="center" sx={{ pt: 2 }}>{i + 1}</TableCell>
                    <TableCell sx={{ pt: 1 }}>
                      <InputTextField
                        hookValue
                        inputHeight={37}
                        placeholder="พนักงานออฟฟิศ, นักธุรกิจ, ..."
                        value={b.label}
                        onchange={(e: any) => setBucket(i, { label: e.target.value })}
                      />
                    </TableCell>
                    <TableCell sx={{ pt: 1 }}>
                      <SelectCheckBoxInput
                        fullWidth
                        searchable
                        renderChips
                        height={37}
                        searchPlaceholder={t('DASHBOARD_TARGETS.OCC.SEARCH', 'พิมพ์เพื่อค้นหาอาชีพ...') as string}
                        options={options}
                        selected={b.careerIds}
                        onChange={(ids: any[]) => setBucket(i, { careerIds: ids as number[] })}
                      />
                    </TableCell>
                    <TableCell sx={{ pt: 2 }}>
                      <Tooltip title={t('DASHBOARD_TARGETS.OCC.REMOVE', 'ลบ') as string}>
                        <IconButton size="small" onClick={() => removeRow(i)}>
                          <img src={icons.btnTrash} alt="delete" style={{ width: 20, height: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {t(
            'DASHBOARD_TARGETS.OCC.HINT',
            `เคล็ดลับ: อาชีพที่ไม่ได้จัดกลุ่มจะถูกรวมเป็น "อื่นๆ" และคนไข้ที่ไม่ระบุอาชีพจะอยู่ใน "ไม่ระบุอาชีพ" บนหน้าแดชบอร์ดอัตโนมัติ (ทั้งหมด ${careers.length} อาชีพในระบบ)`
          )}
          {hasUnsavedChanges && ` · ${t('DASHBOARD_TARGETS.UNSAVED', 'มีการเปลี่ยนแปลงที่ยังไม่บันทึก')}`}
        </Typography>
      </Box>

      <PageModal
        isOpen={confirmReset}
        size="XS"
        title={t('DASHBOARD_TARGETS.RESET_TITLE', { defaultValue: 'ยืนยันรีเซ็ตการแก้ไข' }) as string}
        handleClose={() => setConfirmReset(false)}
        onCancel={{
          onClick: () => setConfirmReset(false),
          title: t('DASHBOARD_TARGETS.PICKER.CANCEL', { defaultValue: 'ยกเลิก' }) as string,
        }}
        onSubmit={{
          onClick: () => {
            setConfirmReset(false)
            doReset()
          },
          title: t('DASHBOARD_TARGETS.RESET_CONFIRM', { defaultValue: 'รีเซ็ต' }) as string,
          type: 'danger',
        }}
      >
        <Typography variant="body2">
          {t('DASHBOARD_TARGETS.RESET_BODY', {
            defaultValue: 'หมายเหตุ: การเปลี่ยนแปลงที่ยังไม่บันทึกทั้งหมดจะถูกรีเซ็ต',
          })}
        </Typography>
      </PageModal>
    </Box>
  )
}
